import type { PurchaseRequest, RequestPriority } from "./domain/models";

export type {
  ProcurementStage,
  PurchaseRequest,
  RequestPriority,
  RequestStatus,
} from "./domain/models";
export { procurementStageOf } from "./domain/workflow";

const procurementStatusPriority: Record<string, number> = {
  "Onaya gönderilmeye hazır": 0,
  "Satın Almaya iletildi": 1,
  "Teklif toplanıyor": 2,
  Revizyon: 3,
  "Düzeltme istendi": 3,
  "Onay bekliyor": 4,
  Reddedildi: 5,
  Onaylandı: 6,
  "İptal edildi": 7,
  Taslak: 8,
};

const turkishMonths: Record<string, number> = {
  oca: 1,
  şub: 2,
  mar: 3,
  nis: 4,
  may: 5,
  haz: 6,
  tem: 7,
  ağu: 8,
  eyl: 9,
  eki: 10,
  kas: 11,
  ara: 12,
};

const priorityOrder: Record<RequestPriority, number> = {
  Kritik: 0,
  Yüksek: 1,
  Normal: 2,
};

function dateOnlyTimestamp(value?: string | null) {
  if (!value) return null;
  const isoMatch = value.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const turkishMatch = value
    .trim()
    .replaceAll(".", "")
    .match(/^(\d{1,2})\s+([A-Za-zÇĞİÖŞÜçğıöşü]+)\s+(\d{4})$/);
  const year = isoMatch?.[1] ?? turkishMatch?.[3];
  const month = isoMatch?.[2]
    ? Number(isoMatch[2])
    : turkishMatch?.[2]
      ? turkishMonths[turkishMatch[2].toLocaleLowerCase("tr-TR")]
      : undefined;
  const day = isoMatch?.[3] ?? turkishMatch?.[1];
  if (!year || !month || !day) return null;
  return Date.UTC(Number(year), month - 1, Number(day));
}

function requestDueTimestamp(request: PurchaseRequest) {
  return (
    dateOnlyTimestamp(request.neededAt) ??
    dateOnlyTimestamp(request.due) ??
    Number.POSITIVE_INFINITY
  );
}

function latestRequestDecisionTimestamp(request: PurchaseRequest) {
  const decisionTimes = (request.approvalChain ?? [])
    .map((approval) => approval.decidedAt)
    .filter((value): value is string => Boolean(value))
    .map((value) => Date.parse(value))
    .filter(Number.isFinite);
  const createdAt = request.createdAt ? Date.parse(request.createdAt) : NaN;
  return Math.max(...decisionTimes, Number.isFinite(createdAt) ? createdAt : 0);
}

export function compareProcurementRequests(
  left: PurchaseRequest,
  right: PurchaseRequest,
) {
  const statusDifference =
    (procurementStatusPriority[left.status] ?? 99) -
    (procurementStatusPriority[right.status] ?? 99);
  if (statusDifference) return statusDifference;
  return priorityOrder[left.priority] - priorityOrder[right.priority];
}

export function compareProcurementWorkQueue(
  left: PurchaseRequest,
  right: PurchaseRequest,
  today = new Date(),
) {
  const terminalStatuses = new Set(["Onaylandı", "Reddedildi", "İptal edildi"]);
  const leftTerminal = terminalStatuses.has(left.status);
  const rightTerminal = terminalStatuses.has(right.status);
  if (leftTerminal && rightTerminal) {
    const completionDifference =
      latestRequestDecisionTimestamp(right) -
      latestRequestDecisionTimestamp(left);
    if (completionDifference) return completionDifference;
    const dueDifference =
      requestDueTimestamp(right) - requestDueTimestamp(left);
    if (Number.isFinite(dueDifference) && dueDifference) return dueDifference;
    return left.id.localeCompare(right.id, "tr");
  }
  if (leftTerminal !== rightTerminal) return leftTerminal ? 1 : -1;

  const correctionStatuses = new Set(["Düzeltme istendi", "Revizyon"]);
  const correctionDifference =
    Number(correctionStatuses.has(right.status)) -
    Number(correctionStatuses.has(left.status));
  if (correctionDifference) return correctionDifference;

  const currentDay = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const leftDue = requestDueTimestamp(left);
  const rightDue = requestDueTimestamp(right);
  const overdueDifference =
    Number(rightDue <= currentDay) - Number(leftDue <= currentDay);
  if (overdueDifference) return overdueDifference;

  const priorityDifference =
    priorityOrder[left.priority] - priorityOrder[right.priority];
  if (priorityDifference) return priorityDifference;
  if (leftDue !== rightDue) return leftDue - rightDue;

  const leftCreated = left.createdAt ? Date.parse(left.createdAt) : NaN;
  const rightCreated = right.createdAt ? Date.parse(right.createdAt) : NaN;
  if (Number.isFinite(leftCreated) && Number.isFinite(rightCreated)) {
    const createdDifference = leftCreated - rightCreated;
    if (createdDifference) return createdDifference;
  }
  return left.id.localeCompare(right.id, "tr");
}

export function formatEvaluationAmount(
  value: number | null | undefined,
  currency = "TRY",
) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatDisplayDate(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  const [, year, month, day] = match;
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))));
}

export function formatDaysAgo(value: string, today = new Date()) {
  const match = value.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  const sourceDay = Date.UTC(Number(year), Number(month) - 1, Number(day));
  const currentDay = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const difference = Math.floor((currentDay - sourceDay) / 86_400_000);
  if (difference < 0) return null;
  if (difference === 0) return "Bugün";
  if (difference === 1) return "Dün";
  return `${difference} gün önce`;
}

export function formatRemainingDays(value: string, today = new Date()) {
  const targetDay = dateOnlyTimestamp(value);
  if (targetDay === null) return null;
  const currentDay = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const difference = Math.round((targetDay - currentDay) / 86_400_000);
  if (difference === 0) return "İhtiyaç bugün";
  if (difference === 1) return "1 gün kaldı";
  if (difference > 1) return `${difference} gün kaldı`;
  if (difference === -1) return "1 gün geçti";
  return `${Math.abs(difference)} gün geçti`;
}
